import React, { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Minus, X, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const ProductModal = ({ open, onClose, item, restaurant }) => {
  const { t } = useLang();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [selAddons, setSelAddons] = useState([]);
  const [removed, setRemoved] = useState([]);

  useEffect(() => {
    if (open) { setQty(1); setSelAddons([]); setRemoved([]); }
  }, [open]);

  const addonsCost = useMemo(() => selAddons.reduce((s, a) => s + (a.price || 0), 0), [selAddons]);
  const totalPrice = useMemo(() => item ? (item.price + addonsCost) * qty : 0, [item, addonsCost, qty]);

  if (!item) return null;

  const toggleAddon = (a) => {
    setSelAddons((prev) => prev.find(x => x.name === a.name) ? prev.filter(x => x.name !== a.name) : [...prev, a]);
  };
  const toggleRemoved = (ing) => {
    setRemoved((prev) => prev.includes(ing) ? prev.filter(x => x !== ing) : [...prev, ing]);
  };

  const handleAdd = () => {
    addItem(restaurant, item, qty, selAddons, removed);
    toast.success(`${item.name} × ${qty}`);
    onClose();
  };

  const hasAddons = item.addons && item.addons.length > 0;
  const hasRemovable = item.removable_ingredients && item.removable_ingredients.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 max-w-lg overflow-hidden gap-0" data-testid="product-modal">
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        {item.image && (
          <div className="relative aspect-[16/10] bg-sand-100">
            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5" data-testid="product-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <h3 className="font-heading font-bold text-navy text-2xl">{item.name}</h3>
            {item.description && <p className="text-navy/70 text-sm mt-1">{item.description}</p>}
            <div className="mt-2 font-semibold text-navy">€{item.price.toFixed(2)}</div>
          </div>

          {hasRemovable && (
            <div className="space-y-2">
              <div className="font-heading font-bold text-navy text-sm flex items-center justify-between">
                <span>{t("customiseTitle")}</span>
                <span className="text-[11px] font-normal text-navy/50">{t("customiseHint")}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {item.removable_ingredients.map((ing) => {
                  const excluded = removed.includes(ing);
                  return (
                    <button
                      key={ing}
                      type="button"
                      onClick={() => toggleRemoved(ing)}
                      className={`flex items-center justify-between gap-2 p-2.5 rounded-xl border text-left transition-all ${excluded ? "bg-coral/10 border-coral" : "bg-white border-navy/10 hover:bg-sand-50"}`}
                      data-testid={`remove-${ing}`}
                    >
                      <span className={`text-xs ${excluded ? "line-through text-coral font-semibold" : "text-navy"}`}>
                        {ing}
                      </span>
                      {excluded
                        ? <span className="text-[10px] font-bold uppercase text-coral shrink-0">{t("no")}</span>
                        : <Check className="w-3.5 h-3.5 text-teal shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {hasAddons && (
            <div className="space-y-2">
              <div className="font-heading font-bold text-navy text-sm">{t("addonsTitle")}</div>
              {item.addons.map((a) => (
                <label key={a.name} className="flex items-center justify-between p-3 rounded-xl border border-navy/10 cursor-pointer hover:bg-sand-50" data-testid={`addon-${a.name}`}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!selAddons.find(x => x.name === a.name)}
                      onChange={() => toggleAddon(a)}
                      className="w-4 h-4 accent-coral"
                    />
                    <span className="text-sm">{a.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-coral">+€{a.price.toFixed(2)}</span>
                </label>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 sticky bottom-0 bg-white pb-1">
            <div className="flex items-center gap-2 bg-sand-100 rounded-full p-1">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full bg-white flex items-center justify-center" data-testid="qty-decrease">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center" data-testid="qty-increase">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <Button onClick={handleAdd} className="btn-coral" data-testid="add-to-basket-btn">
              {t("addToBasket")} • €<span data-testid="live-total">{totalPrice.toFixed(2)}</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
